import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File, X, CheckCircle, AlertCircle, Loader, FileSpreadsheet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { filesApi } from '../utils/api';
import posthog from 'posthog-js';

const FileUpload = ({ onUploadComplete }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [userFile, setUserFile] = useState(null);
  const [userfileEnabled, setUserfileEnabled] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [processingStatus, setProcessingStatus] = useState('');
  const processingMode = 'simple'; // Always use simple mode

  useEffect(() => {
    filesApi.getUploadConfig()
      .then(res => setUserfileEnabled(res.data.userfile_enabled))
      .catch(() => {});
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setFiles(prev => [...prev, ...acceptedFiles]);
      setUploadComplete(false);
      setUploadError(null);
    },
    multiple: true,
  });

  const userFileDropzone = useDropzone({
    onDrop: (accepted) => {
      if (accepted.length > 0) {
        setUserFile(accepted[0]);
        setUploadError(null);
      }
    },
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: false,
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup is handled by the polling function itself
      setProcessing(false);
    };
  }, []);

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const pollProcessingStatus = async (fileId, pollInterval = 2000, maxPollTime = 600000) => {
    const startTime = Date.now();
    let currentInterval = pollInterval;
    let pollCount = 0;

    const poll = async () => {
      try {
        // Check if we've exceeded max polling time (default: 10 minutes)
        if (Date.now() - startTime > maxPollTime) {
          console.error('Polling timeout exceeded');
          setProcessing(false);
          setUploadError(t('fileUpload.processingTimeout'));
          return;
        }

        const response = await filesApi.getProcessingStatus(fileId);
        const status = response.data.processing_status;
        setProcessingStatus(status);
        pollCount++;

        if (status === 'completed') {
          // Processing complete
          console.log(`Processing completed after ${pollCount} polls`);
          posthog.capture('processing_completed', {
            rows_count: response.data.rows_count,
            total_tokens: response.data.total_tokens,
            execution_seconds: response.data.execution_seconds,
          });
          setProcessing(false);
          setUploadComplete(true);
          onUploadComplete?.(response.data);
        } else if (status === 'failed') {
          // Processing failed
          console.error('Processing failed:', response.data.error_message);
          posthog.capture('processing_failed', { error: response.data.error_message });
          setProcessing(false);
          setUploadError(response.data.error_message || t('fileList.status.failed'));
        } else {
          // Still processing - schedule next poll with exponential backoff
          // Start at 2s, increase to max 10s
          currentInterval = Math.min(currentInterval * 1.2, 10000);
          setTimeout(poll, currentInterval);
        }
      } catch (error) {
        console.error('Failed to check status:', error);
        setProcessing(false);
        setUploadError(t('fileUpload.statusCheckFailed'));
      }
    };

    // Start polling
    poll();
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadError(null);
    setUploadComplete(false);

    try {
      console.log(`Uploading ${files.length} files with ${processingMode} mode...`);

      // Upload files - server returns 202 Accepted immediately
      const response = await filesApi.uploadFiles(files, processingMode, userFile);
      const fileSessionId = response.data.id;

      posthog.capture('files_uploaded', {
        file_count: files.length,
        processing_mode: processingMode,
        total_size_mb: parseFloat((files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2)),
      });

      console.log(`Upload accepted. Session ID: ${fileSessionId}. Starting to poll status...`);

      setUploading(false);
      setProcessing(true);
      setFiles([]);
      setUserFile(null);
      setProcessingStatus('pending');

      // Start polling with exponential backoff (starts at 2s, increases to max 10s)
      // Max polling time: 10 minutes
      pollProcessingStatus(fileSessionId, 2000, 600000);

    } catch (error) {
      console.error('Upload failed:', error);
      const detail = error.response?.data?.detail;
      const errorMessage = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(e => e.msg || JSON.stringify(e)).join(', ')
          : error.message || 'Upload failed. Please try again.';
      setUploadError(errorMessage);
      setUploading(false);
      setProcessing(false);
    }
  };

  return (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 p-6">
      <h2 className="text-xl font-bold text-neutral-800 mb-6 flex items-center">
        <Upload className="h-6 w-6 mr-3 text-primary-500" />
        {t('fileUpload.title')}
      </h2>

      <div className={userfileEnabled ? 'space-y-5' : ''}>
        {/* Document files */}
        <div>
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-300 ${
              isDragActive
                ? 'border-primary-500 bg-primary-50/50 scale-[1.02]'
                : 'border-neutral-300 hover:border-primary-400 hover:bg-primary-50/30'
            }`}
          >
            <input {...getInputProps()} />
            <div className={`mx-auto h-16 w-16 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 ${
              isDragActive ? 'bg-primary-500 text-white' : 'bg-neutral-100 text-neutral-400'
            }`}>
              <Upload className="h-8 w-8" />
            </div>
            <p className="text-sm font-medium text-neutral-700 mb-2">
              {isDragActive
                ? t('fileUpload.dragDropActive')
                : t('fileUpload.dragDrop')
              }
            </p>
            <p className="text-xs text-neutral-500">
              {t('fileUpload.supportedFormats')}
            </p>
          </div>

          {/* Selected document files */}
          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              {files.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-white rounded-xl p-3 border border-neutral-200/50">
                  <div className="flex items-center">
                    <div className="bg-primary-100 p-2 rounded-lg mr-3">
                      <File className="h-4 w-4 text-primary-600" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-neutral-800">{file.name}</span>
                      <span className="text-xs text-neutral-500 ml-2 bg-neutral-200 px-2 py-1 rounded-full">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="text-error-500 hover:text-error-700 p-1 rounded-lg hover:bg-error-50 transition-colors duration-200"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 2: User's Excel file */}
        {userfileEnabled && (
          <div>
            <h3 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-primary-500" />
              {t('fileUpload.userFileTitle')}
            </h3>
            {userFile ? (
              <div className="flex items-center justify-between bg-neutral-50/70 rounded-xl p-4 border border-neutral-200/50">
                <div className="flex items-center">
                  <div className="bg-primary-100 p-2 rounded-lg mr-3">
                    <FileSpreadsheet className="h-4 w-4 text-primary-600" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-neutral-800">{userFile.name}</span>
                    <span className="text-xs text-neutral-500 ml-2 bg-neutral-200 px-2 py-1 rounded-full">
                      {(userFile.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setUserFile(null)}
                  className="text-error-500 hover:text-error-700 p-1 rounded-lg hover:bg-error-50 transition-colors duration-200"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div
                {...userFileDropzone.getRootProps()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-300 ${
                  userFileDropzone.isDragActive
                    ? 'border-primary-500 bg-primary-50/50'
                    : 'border-neutral-300 hover:border-primary-400 hover:bg-primary-50/30'
                }`}
              >
                <input {...userFileDropzone.getInputProps()} />
                <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 text-neutral-400" />
                <p className="text-sm text-neutral-600">
                  {t('fileUpload.userFileDragDrop')}
                </p>
                <p className="text-xs text-neutral-400 mt-1">
                  {t('fileUpload.userFileFormats')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Processing Mode Toggle - COMMENTED OUT */}
      {/* <div className="mt-6 bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-4 border border-primary-200">
        <h3 className="text-sm font-semibold text-neutral-800 mb-3">{t('fileUpload.processingModeTitle')}</h3>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setProcessingMode('simple')}
            className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 ${
              processingMode === 'simple'
                ? 'border-primary-500 bg-primary-100 shadow-md'
                : 'border-neutral-300 bg-white hover:border-primary-300'
            }`}
          >
            <div className={`text-2xl mb-2 ${processingMode === 'simple' ? 'text-primary-600' : 'text-neutral-400'}`}>
              ⚡
            </div>
            <span className={`text-sm font-semibold ${processingMode === 'simple' ? 'text-primary-700' : 'text-neutral-600'}`}>
              {t('fileUpload.processingMode.simple')}
            </span>
            <span className="text-xs text-neutral-500 mt-1 text-center">
              {t('fileUpload.modeDescriptions.simple')}
            </span>
          </button>

          <button
            onClick={() => setProcessingMode('full')}
            className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 ${
              processingMode === 'full'
                ? 'border-primary-500 bg-primary-100 shadow-md'
                : 'border-neutral-300 bg-white hover:border-primary-300'
            }`}
          >
            <div className={`text-2xl mb-2 ${processingMode === 'full' ? 'text-primary-600' : 'text-neutral-400'}`}>
              📋
            </div>
            <span className={`text-sm font-semibold ${processingMode === 'full' ? 'text-primary-700' : 'text-neutral-600'}`}>
              {t('fileUpload.processingMode.full')}
            </span>
            <span className="text-xs text-neutral-500 mt-1 text-center">
              {t('fileUpload.modeDescriptions.full')}
            </span>
          </button>
        </div>
      </div> */}

      {/* Upload Button */}
      {files.length > 0 && (
        <div className="mt-6">
          <button
            onClick={handleUpload}
            disabled={uploading || processing}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 px-4 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-200"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                {t('fileUpload.uploading')}
              </>
            ) : (
              <>
                <Upload className="h-5 w-5 mr-3" />
                {t('fileUpload.uploadButton')}
              </>
            )}
          </button>
        </div>
      )}

      {/* Processing Status */}
      {processing && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center animate-slide-up">
          <Loader className="h-6 w-6 text-blue-600 mr-3 animate-spin" />
          <div>
            <span className="text-sm font-medium text-blue-700">
              {processingStatus === 'pending' && t('fileUpload.processingPending')}
              {processingStatus === 'processing' && t('fileUpload.processingActive')}
            </span>
          </div>
        </div>
      )}

      {/* Success Message */}
      {uploadComplete && (
        <div className="mt-6 bg-success-50 border border-success-200 rounded-xl p-4 flex items-center animate-slide-up">
          <CheckCircle className="h-6 w-6 text-success-600 mr-3" />
          <span className="text-sm font-medium text-success-700">{t('fileUpload.uploadSuccess')}</span>
        </div>
      )}

      {/* Error Message */}
      {uploadError && (
        <div className="mt-6 bg-error-50 border border-error-200 rounded-xl p-4 flex items-start animate-slide-up">
          <AlertCircle className="h-6 w-6 text-error-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <span className="text-sm font-medium text-error-700 block">{t('fileUpload.uploadFailed')}</span>
            <span className="text-xs text-error-600 mt-1 block">{uploadError}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;